import { Component, EventEmitter, Output } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FunctionsService } from 'src/app/data/functions.service';
import { AdicionaNegociacaoCompostaComponent } from '../adiciona-negociacao-composta/adiciona-negociacao-composta.component';

/**
 * Ponto de entrada, separado do chat de busca contextual
 * (`FullscreenModalContextualSearchComponent`), para o comprador subir um
 * arquivo com uma lista de compras corporativa (Excel/CSV/PDF).
 *
 * Fluxo: seleciona o arquivo aqui -> abre imediatamente a tela de revisão
 * (`AdicionaNegociacaoCompostaComponent`, já em estado de processamento) ->
 * chama `POST /produtos/v1/bulk-list-import` -> popula a revisão assim que a
 * resposta chega (ou mostra o erro, se falhar), sem duplicar o estado de
 * "carregando" em dois modais ao mesmo tempo.
 */
const EXTENSOES_ACEITAS = ['.xlsx', '.xls', '.csv', '.pdf'];

@Component({
  selector: 'app-upload-lista-compras',
  templateUrl: './upload-lista-compras.component.html',
  styleUrls: ['./upload-lista-compras.component.css']
})
export class UploadListaComprasComponent {
  isOpen = true;
  selectedFile: File | null = null;
  error: string | null = null;
  enviando = false;
  @Output() closed = new EventEmitter<void>();

  constructor(
    public dialogRef: MatDialogRef<UploadListaComprasComponent>,
    private dialog: MatDialog,
    private functions: FunctionsService,
  ) { }

  onFileSelected(event: { files?: File[] } | Event): void {
    this.error = null;
    const file = this.extractFile(event);
    if (!file) {
      return;
    }

    const nomeArquivo = file.name.toLowerCase();
    const extensaoValida = EXTENSOES_ACEITAS.some(extensao => nomeArquivo.endsWith(extensao));
    if (!extensaoValida) {
      this.selectedFile = null;
      this.error = 'lista_compras_arquivo_invalido';
      return;
    }

    this.selectedFile = file;
  }

  private extractFile(event: { files?: File[] } | Event): File | null {
    const possivelFileList = (event as { files?: File[] })?.files;
    if (possivelFileList?.length) {
      return possivelFileList[0];
    }
    const input = (event as Event)?.target as HTMLInputElement;
    if (input?.files?.length) {
      return input.files[0];
    }
    return null;
  }

  removerArquivo(): void {
    this.selectedFile = null;
    this.error = null;
  }

  enviarLista(): void {
    if (!this.selectedFile || this.enviando) {
      return;
    }

    this.enviando = true;
    const arquivo = this.selectedFile;

    // Abre a revisão já em estado de processamento (loading próprio, cobrindo
    // a fase de upload/extração) para não fazer o comprador esperar parado
    // neste modal menor até a IA terminar de ler o arquivo.
    const revisaoDialogRef = this.dialog.open(AdicionaNegociacaoCompostaComponent, {
      panelClass: 'fullscreen-dialog',
      backdropClass: 'fullscreen-backdrop',
      width: '100vw !important',
      height: '100vh !important',
      data: { initialProducts: [], isBulkImport: true, pending: true },
    });

    this.closeModal();

    this.functions.bulkListImport(arquivo).subscribe({
      next: (response) => {
        const { prompt_id, itens } = response.data;
        const initialProducts = (itens || []).map(item => {
          const primeiroCandidato = item.candidatos?.[0];
          return {
            ean: item.ean || primeiroCandidato?.ean || '',
            nome: item.nome || primeiroCandidato?.nome || '',
            imagemUrl: primeiroCandidato?.imagem?.url || '',
            prompt_id,
            quantidade: item.quantidade || 1,
            valor_pretendido: item.valor_pretendido ?? undefined,
            candidatos: item.candidatos || [],
          };
        });
        revisaoDialogRef.componentInstance.receiveBulkImportResult(initialProducts);
      },
      error: (err) => {
        const message = err?.error?.message || 'lista_compras_erro_generico';
        revisaoDialogRef.componentInstance.receiveBulkImportError(message);
      }
    });
  }

  closeModal(): void {
    this.isOpen = false;
    this.closed.emit();
    this.dialogRef.close();
  }
}
